<template>
    <div>
        <div v-if="$slots.tooltipTarget" ref="tooltipTargetRef">
            <slot name="tooltipTarget" />
        </div>

        <div class="d-none" data-testid="tooltip-templates">
            <div
                v-if="$slots.tooltipTarget && $slots.tooltipContent"
                class="tooltip-content-container"
                ref="tooltipContentRef"
                @click="onClickInTooltip"
            >
                <button
                    v-if="withCloseTooltipButton"
                    type="button"
                    class="btn-close"
                    aria-label="Fermer"
                    @click="closeTooltip"
                >
                    <span aria-hidden="true" class="icon icon-delete"></span>
                </button>
                <slot name="tooltipContent" />
            </div>
        </div>
    </div>
</template>

<script>
import tippy from 'tippy.js';

const placementValues = ['top', 'top-start', 'top-end', 'right', 'right-start', 'right-end', 'bottom', 'bottom-start', 'bottom-end', 'left', 'left-start', 'left-end', 'auto', 'auto-start', 'auto-end'];
const triggerValues = ['click mouseenter', 'click', 'mouseenter'];
const flipPlacementValues = ['top', 'right', 'bottom', 'left'];

export default {
    name: "CustomTooltipContainer",
    props: {
        tooltipClickedStayOpened: { type: Boolean, default: true },
        placement: {
            type: String,
            default: 'bottom-end',
            validator(value, props) {
                // The value must match one of these strings
                return placementValues.includes(value)
            }
        },
        trigger: {
            type: String,
            default: 'click mouseenter',
            validator(value, props) {
                // The value must match one of these strings
                return triggerValues.includes(value)
            }
        },
        flipPlacements: {
            type: Array,
            default: () => {
                return ['bottom'];
            },
            validator(value, props) {
                if (Array.isArray(value) && (value.length > 0)) {
                    value.forEach(item => {
                        if (!flipPlacementValues.includes(item)) {
                            return false;
                        }
                    });
                    return true;
                } else {
                    return false;
                }
            }
        },
        interactive: {
            type: Boolean,
            default: true
        },
        interactiveBorder: {
            type: Number,
            default: 15
        },
        maxWidth: {
            type: Number,
            default: 300
        },
        withCloseTooltipButton: {
            type: Boolean,
            default: true
        },
        openByDefault: {
            type: Boolean,
            default: false
        },
        openByDefaultDelay: {
            type: Number,
            default: 1000
        },
    },
    data() {
        return {
            tippyComplexInstance: undefined,
        }
    },
    mounted() {
        this.addTippyTooltip();
        if (this.openByDefault) {
            // S'il est demandé d'ouvrir la tooltip par défaut
            // on l'ouvre après un petit délai
            // et on ne veut plus qu'elle se ferme lors des mouveleave

            setTimeout(() => {
                if (this.tippyComplexInstance) {
                    this.tippyComplexInstance.show();
                    this.forceClickTriggerWhileOpened();
                }
            }, this.openByDefaultDelay)
        }
    },
    updated() {
        this.updateTippyTooltip();
    },
    methods: {
        closeTooltip(event) {
            if (this.tippyComplexInstance) {
                this.tippyComplexInstance.hide();
                event.stopPropagation();
            }
        },
        onClickInTooltip(event) {
            // Quand la tooltip est interactive
            // si elle a été ouverte par un hover et que le booleen tooltipClickedStayOpened est positionné à true
            // lorsque cette tooltip a été cliquée
            // on ne veut plus qu'elle se ferme lors des mouveleave

            if (this.tooltipClickedStayOpened && this.tippyComplexInstance && this.tippyComplexInstance.props && this.tippyComplexInstance.props.interactive) {
                this.forceClickTriggerWhileOpened();
            }
        },
        forceClickTriggerWhileOpened() {
            const tooltipContent = this.$refs.tooltipContentRef;
            if (tooltipContent) {
                const initialTrigger = this.trigger;
                if (this.tippyComplexInstance.props.trigger.indexOf('mouseenter') >= 0) {
                    this.tippyComplexInstance.setProps({
                        trigger: 'click',
                        interactiveDebounce: 101,
                        onHide(instance, event) {
                            instance.setProps({
                                trigger: initialTrigger,
                                interactiveDebounce: 100,
                                onHide(instance, event) {},
                            });
                        },
                    });
                }
            }
        },
        updateTippyTooltip() {
            if (this.tippyComplexInstance) {
                this.tippyComplexInstance.destroy();
                this.addTippyTooltip();
            }
        },
        addTippyTooltip() {
            const tooltipContent = this.$refs.tooltipContentRef;
            const tooltipTarget = this.$refs.tooltipTargetRef;
            const showTooltipWhenCreated = this.openByDefault;

            if (tooltipTarget) {

                this.tippyComplexInstance = tippy(tooltipTarget, {
                    content: tooltipContent,
                    trigger: this.trigger,
                    // ajout du theme without-padding pour permettre des backgrounds sur l'ensemble de la tippy tooltip
                    theme: 'light without-padding',
                    placement: this.placement,
                    maxWidth: this.maxWidth,
                    zIndex: 999,
                    interactive: this.interactive,
                    // taille d'une bordure invisible autour de la tippy tooltip conservant l'interactivite
                    interactiveBorder: this.interactiveBorder,
                    appendTo: 'parent',
                    popperOptions: {
                        strategy: 'fixed',
                        modifiers: [
                            {
                                name: 'flip',
                                options: {
                                    // interdiction du flip de la tippy tooltip au dessus de la reference
                                    fallbackPlacements: this.flipPlacements,
                                },
                            }
                        ],
                    },
                });

            }
        },
    }
}
</script>

<style lang="scss">
@import '../../../../../../../front/styles/common/tippy/theme-myshop-light-without-padding.scss';
@import '../../../../../../../../../node_modules/tippy.js/themes/light-border.css';
@import '../../../../../../../../../node_modules/tippy.js/themes/light.css';
@import '../../../../../../../../../node_modules/tippy.js/dist/tippy.css';

.tooltip-content-container > .btn-close::before {
    content: none
}

.tooltip-content-container > .btn-close {
    position: absolute;
    float: right;
    right: 0;
    opacity: 1;
    border: 0;
}

.tooltip-content-container > .btn-close:focus,
.tooltip-content-container > .btn-close:hover {
    color: #555;
}

.tooltip-content-container > .btn-close:active {
    color: #f16e00;
}

.ob1-sosh .tooltip-content-container > .btn-close:focus,
.ob1-sosh .tooltip-content-container > .btn-close:hover {
    color: #eb0450;
}

.ob1-sosh .tooltip-content-container > .btn-close:active {
    color: #000;
}

.ob1-sosh .tippy-box {
    border-radius: .625rem;
}

</style>
